import React from 'react';
import { Collapse, InputAdornment, Typography } from '@mui/material';
import { FormInputNumber, Spacer, ToggleInput, Row } from '../../../components';
import FormFieldRow, { toggleButtonStyle } from './FormFieldRow';
import {
  RevShareFields,
  RevShareFormValues,
  RevShareMethod,
  getRevShareAnnotation,
} from '../../../data/rev-share/helpers/revShareSettingsUtils';

interface RevShareMethodFieldsProps {
  revShareMethod: RevShareMethod | undefined;
  readOnly?: boolean;
  inheritedFrom: string;
  inheritedValues?: RevShareFields;
}

const RevShareMethodFields: React.FC<RevShareMethodFieldsProps> = ({
  revShareMethod,
  readOnly = false,
  inheritedFrom,
  inheritedValues,
}) => {
  const isInheritedDisabled = readOnly && !revShareMethod;
  const shouldShowDetail = !readOnly && Boolean(inheritedFrom);

  const annotation = shouldShowDetail
    ? getRevShareAnnotation(inheritedFrom, inheritedValues)
    : undefined;

  const methodSubLabel = isInheritedDisabled
    ? `(${inheritedFrom} opted out of fee share)`
    : annotation?.revShareMethod;

  return (
    <>
      <FormFieldRow label='Fee share method' subLabel={methodSubLabel}>
        <ToggleInput<RevShareFormValues>
          fieldName='revShareMethod'
          items={['% of sale', 'Fixed $ amt']}
          exclusive
          buttonStyle={toggleButtonStyle}
          readonly={readOnly}
          required={false}
        />
      </FormFieldRow>

      <Collapse in={revShareMethod === '% of sale'} timeout={300} unmountOnExit>
        <Spacer height='s25' />

        <FormFieldRow label='Base amount'>
          <Row style={{ alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <FormInputNumber<RevShareFormValues>
              fieldName='baseAmount'
              placeholder='0'
              decimalPlaces={2}
              inputStyle={{ maxWidth: '120px' }}
              startAdornment={<InputAdornment position='start'>$</InputAdornment>}
              blurOnEnter
              fixedDecimalsOnBlur
              readonly={readOnly}
              readonlyStyle='compact'
            />
            {annotation?.baseAmount ? (
              <Typography variant='p12' color='info.disabled' sx={{ whiteSpace: 'nowrap' }}>
                {annotation.baseAmount}
              </Typography>
            ) : null}
          </Row>
        </FormFieldRow>

        <Spacer height='s25' />

        <FormFieldRow label='Fee share percentage'>
          <Row style={{ alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <FormInputNumber<RevShareFormValues>
              fieldName='revSharePct'
              placeholder='0'
              max={100}
              decimalPlaces={2}
              inputStyle={{ maxWidth: '120px' }}
              endAdornment={<InputAdornment position='end'>%</InputAdornment>}
              blurOnEnter
              readonly={readOnly}
              readonlyStyle='compact'
            />
            {annotation?.revSharePct ? (
              <Typography variant='p12' color='info.disabled' sx={{ whiteSpace: 'nowrap' }}>
                {annotation.revSharePct}
              </Typography>
            ) : null}
          </Row>
        </FormFieldRow>
      </Collapse>

      <Collapse in={revShareMethod === 'Fixed $ amt'} timeout={300} unmountOnExit>
        <Spacer height='s25' />

        <FormFieldRow label='Fee share amount'>
          <Row style={{ alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <FormInputNumber<RevShareFormValues>
              fieldName='fixedAmount'
              placeholder='0'
              decimalPlaces={2}
              inputStyle={{ maxWidth: '120px' }}
              startAdornment={<InputAdornment position='start'>$</InputAdornment>}
              blurOnEnter
              fixedDecimalsOnBlur
              readonly={readOnly}
              readonlyStyle='compact'
            />
            {annotation?.fixedAmount ? (
              <Typography variant='p12' color='info.disabled' sx={{ whiteSpace: 'nowrap' }}>
                {annotation.fixedAmount}
              </Typography>
            ) : null}
          </Row>
        </FormFieldRow>
      </Collapse>
    </>
  );
};

export default RevShareMethodFields;
